import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, FileText, Leaf, Shield, Users, Settings } from 'lucide-react';
import {
    HistoryPageLayout,
    AuditLogTable,
    type LenseItem,
} from '@hofsync/design-system';
import { useAuditEvents } from '../hooks/useAuditEvents';

// German history categories — these IDs are the *contract* between the lens
// panel and the backend's `event_type_mapper.category_to_event_types`. Adding
// or renaming a leaf without a matching mapper update produces a leaf with
// no event_type mapping (server returns an empty page per ADR-003).
const historyCategoriesDE: LenseItem[] = [
    { id: 'all', name: 'Alle Aktivitäten', icon: <Clock /> },
    {
        id: 'documents',
        name: 'Dokumente',
        icon: <FileText />,
        children: [
            { id: 'uploads', name: 'Uploads' },
            { id: 'downloads', name: 'Downloads' },
            { id: 'edits', name: 'Bearbeitungen' },
            { id: 'deletions', name: 'Löschungen' },
            { id: 'shares', name: 'Freigaben' },
        ],
    },
    {
        id: 'farm-data',
        name: 'Betriebsdaten',
        icon: <Leaf />,
        children: [
            { id: 'animals', name: 'Tiere' },
            { id: 'fields', name: 'Flächen' },
            { id: 'equipment', name: 'Maschinen' },
            { id: 'buildings', name: 'Gebäude' },
            { id: 'staff', name: 'Personal' },
            { id: 'inventory', name: 'Inventar' },
        ],
    },
    {
        id: 'compliance',
        name: 'Compliance',
        icon: <Shield />,
        children: [
            { id: 'reports-submitted', name: 'Eingereichte Berichte' },
            { id: 'audits', name: 'Prüfungen' },
            { id: 'deadlines', name: 'Fristen' },
            { id: 'inspections', name: 'Kontrollen' },
        ],
    },
    {
        id: 'user-activity',
        name: 'Benutzeraktivität',
        icon: <Users />,
        children: [
            { id: 'logins', name: 'Anmeldungen' },
            { id: 'profile-changes', name: 'Profiländerungen' },
            { id: 'settings', name: 'Einstellungen' },
            { id: 'invitations', name: 'Einladungen' },
        ],
    },
    {
        id: 'system',
        name: 'System',
        icon: <Settings />,
        children: [
            { id: 'notifications', name: 'Benachrichtigungen' },
            { id: 'sync', name: 'Synchronisierung' },
            { id: 'integrations', name: 'Integrationen' },
            { id: 'backups', name: 'Backups' },
        ],
    },
];

const HistoryPage: React.FC = () => {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);

    const handleCategorySelect = (categoryId: string, subcategoryId: string | null) => {
        setSelectedCategory(categoryId);
        setSelectedSubcategory(subcategoryId);
    };

    const { entries, isLoading, isError, refetch } = useAuditEvents({
        scope: 'global',
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        subcategory: selectedSubcategory ?? undefined,
    });

    return (
        <HistoryPageLayout
            categories={historyCategoriesDE}
            selectedCategory={selectedCategory}
            selectedSubcategory={selectedSubcategory}
            onCategorySelect={handleCategorySelect}
            rootLabel={t('history.title', 'Verlauf')}
        >
            {isError ? (
                <div role="alert" style={{ padding: '1rem' }}>
                    <p>{t('history.loadError', 'Aktivitäten konnten nicht geladen werden.')}</p>
                    <button type="button" onClick={() => refetch()}>
                        {t('common.retry', 'Erneut versuchen')}
                    </button>
                </div>
            ) : (
                <AuditLogTable
                    entries={entries}
                    pageSize={25}
                    emptyMessage={
                        isLoading
                            ? t('history.loading', 'Aktivitäten werden geladen…')
                            : t('history.noEntries', 'Keine Einträge in dieser Kategorie')
                    }
                />
            )}
        </HistoryPageLayout>
    );
};

export default HistoryPage;
